import React, { type ReactElement } from 'react';
import { motion } from 'framer-motion';
import {
  PAWN_SCALE,
  PAWN_OFFSET_X,
  PAWN_OFFSET_Y,
  PAWN_POSITIONS,
  type PawnPositionKey
} from '../constants/constants';
import type { PawnProps } from '../../../constants/types';
import { CLICKABLE_PAWN_ANIMATION } from '../../../animations/constants';

/**
 * Renders a pawn at a specific position within a base
 *
 * This utility function centralizes the pawn rendering logic used across
 * all Base components (Red, Blue, Green, Yellow). It handles positioning,
 * scaling, conditional rendering, animation, and click handling.
 *
 * @param position - Position key (pawn1, pawn2, pawn3, or pawn4)
 * @param show - Whether to render the pawn at this position
 * @param PawnComponent - Color-specific Pawn component to render (RedPawn, BluePawn, etc.)
 * @param pawnProps - Props to pass through to the Pawn component
 * @param onClick - Optional click handler for pawn selection
 * @param shouldAnimate - Whether to show bounce animation (default: false)
 * @returns Positioned pawn SVG group or null if not shown
 *
 * @example
 * ```tsx
 * // In RedBase.tsx with animation and click handler
 * {renderPawnAtPosition('pawn1', pawn1, RedPawn, {...}, handleClick, true)}
 * ```
 */
export function renderPawnAtPosition(
  position: PawnPositionKey,
  show: boolean,
  PawnComponent: React.ComponentType<PawnProps>,
  pawnProps?: Partial<PawnProps>,
  onClick?: () => void,
  shouldAnimate?: boolean
): ReactElement | null {
  if (!show) return null;

  const { cx, cy } = PAWN_POSITIONS[position];
  const translateX = cx - PAWN_OFFSET_X;
  const translateY = cy - PAWN_OFFSET_Y;

  // Use two nested groups: outer for positioning/scale, inner for animation
  return (
    <g
      transform={`translate(${translateX.toString()}, ${translateY.toString()}) scale(${PAWN_SCALE.toString()})`}
    >
      <motion.g
        onClick={onClick}
        animate={shouldAnimate ? CLICKABLE_PAWN_ANIMATION : undefined}
        style={{
          cursor: onClick ? 'pointer' : 'default'
        }}
      >
        <PawnComponent {...pawnProps} />
      </motion.g>
    </g>
  );
}
